import { useTheme, ThemeType } from '@/contexts/ThemeContext';

// Base theme color definitions
const baseThemeColors = {
    cblocks: {
        // Core colors - uses custom Tailwind colors defined in app.css @theme
        primary: 'cb-primary',
        primaryBg: 'bg-cb-primary',
        primaryHover: 'hover:bg-cb-secondary',
        primaryHoverClass: 'cb-secondary',
        accent: 'cb-primary',
        accentBg: 'bg-cb-light',
        accentText: 'text-cb-text',
        border: 'border-cb-border',
        headerBg: 'bg-cb-lighter',
        headerHover: 'hover:bg-cb-light',
        iconBg: 'bg-cb-light',
        iconColor: 'text-cb-primary',
        topicHover: 'hover:bg-cb-lighter',
        progressBar: '#FF6202', // hex for progress components that need raw color
        // Button styles
        buttonPrimary: 'bg-cb-primary hover:bg-cb-secondary',
        buttonPrimaryFocus: 'focus:ring-cb-primary',
        buttonSecondary: 'text-cb-primary hover:bg-cb-light',
        // Input styles
        inputFocus: 'focus:border-cb-primary focus:ring-cb-primary/20',
        // Badge styles
        badgeBg: 'bg-cb-light',
        badgeText: 'text-cb-text',
        // Hover styles
        hoverText: 'group-hover:text-cb-primary',
        // Gradient styles
        gradientFrom: 'from-cb-accent',
        gradientVia: 'via-cb-primary',
        gradientTo: 'to-cb-secondary',
    },
    cfinance: {
        primary: 'indigo-600',
        primaryBg: 'bg-indigo-600',
        primaryHover: 'hover:bg-indigo-700',
        primaryHoverClass: 'indigo-700',
        accent: 'indigo-500',
        accentBg: 'bg-indigo-100',
        accentText: 'text-indigo-700',
        border: 'border-indigo-200',
        headerBg: 'bg-indigo-50',
        headerHover: 'hover:bg-indigo-100',
        iconBg: 'bg-indigo-100',
        iconColor: 'text-indigo-600',
        topicHover: 'hover:bg-indigo-50',
        progressBar: '#4f46e5', // hex for progress components
        // Button styles
        buttonPrimary: 'bg-indigo-600 hover:bg-indigo-700',
        buttonPrimaryFocus: 'focus:ring-indigo-500',
        buttonSecondary: 'text-indigo-600 hover:bg-indigo-50',
        // Input styles
        inputFocus: 'focus:border-indigo-500 focus:ring-indigo-500/20',
        // Badge styles
        badgeBg: 'bg-indigo-100',
        badgeText: 'text-indigo-800',
        // Hover styles
        hoverText: 'group-hover:text-indigo-600',
        // Gradient styles
        gradientFrom: 'from-indigo-300',
        gradientVia: 'via-purple-200',
        gradientTo: 'to-pink-300',
    },
} as const;

// Type for the base theme colors
export type BaseThemeColors = typeof baseThemeColors[ThemeType];

// Type for component-specific color extensions
export type ThemeColorExtensions = Partial<Record<string, string>>;

// Combined theme colors type
export type ThemeColors = BaseThemeColors & ThemeColorExtensions;

/**
 * Hook to get theme colors with optional component-specific extensions
 * @param extensions - Optional component-specific color overrides/additions
 * @returns Complete theme color object for current theme
 */
export const useThemeColors = (extensions?: ThemeColorExtensions): ThemeColors => {
    const { theme } = useTheme();
    const baseColors = baseThemeColors[theme] || baseThemeColors.cfinance;
    
    // Merge base colors with any component-specific extensions
    return {
        ...baseColors,
        ...extensions,
    };
};

export default useThemeColors;